import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
} from 'react';
import { useIntl } from 'umi';
import { Justify, Button } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import SavePopup from '../SavePopup';
import type { FilterCommonType, OperateShowItemType } from '../../types';
import useRenderComponent from './useRenderComponent';
import HistorySelect from '../HistorySelect';
import CardCommon from '../../../CardCommon';
import './index.scss';
import { FilterItemClassName } from '../../constants';
import { defaultFilter } from '../../constants/filterOptions';
import { packGameStore } from '../../constants/group';
import { getFilterName, saveFilterName } from '../../utils/storage';

import StopIcon from '@op/assets/svg/stop-circle.svg';
import { addSubmitBuriedPoint } from '../../utils/point';
import BubbleCommon, {
  ChangeFilterShowTips,
  TipsMapType,
} from '@op/components/BubbleCommon';
import { NO_DATE } from '@op/constants';
import classNames from 'classnames';

/* 复杂下拉需要开发处理 */
export default function FilterBox({
  close,
  filterFixed,
  confirm,
  setFilterName,
  userInfo,
  gameInfo,
  hasLaunchTime,
  operateOptions,
  keyOperateShowOptions = ['confirm', 'reset'],
  lang,
  filterName,
  endElement = null,
  filter = defaultFilter,
  renderExcludeFilter,
  historyConfig,
  noLimitation,
  isAccountRegion,
  historyFilterChange,
  finallyDealwithHistoryValue,
  isTile = false,
  confirmRef,
  showMore,
  toggleShowMore,
  isPreviews = false,
  allPreview= false,
  beforeSaveFilterSetting = (data) => data,
  onlySaveFilters,
  toggleSaveBtnDisable,
  confirmDisabledOnChange,
  disabledTips,
  wholeGameInfo,
  resetBtnText = 'Reset',
  resetBtnTips,
  resetDisabled,
  resetLoading,
  confirmLoading,
  onStop,
  ...otherProps
}: Omit<FilterCommonType, 'tips' | 'setFilterSelect' | 'operateShowOptions'> & {
  close?: (isHeaderChange?: boolean) => void;
  filterFixed?: boolean;
  showMore: boolean;
  toggleShowMore: () => void;
  setFilterName: (name?: string) => void;
  filterName?: string;
  userInfo: any;
  gameInfo: any;
  wholeGameInfo: any;
  operateOptions: Array<OperateShowItemType>;
  disabledTips?: TipsMapType;
  confirmLoading?: boolean;
  allPreview?:boolean;
}) {
  const [disabledConfirm, setDisabledConfirm] = useState(false);

  function changeConfirmDisabled(value: boolean) {
    setDisabledConfirm(value);
    confirmDisabledOnChange?.(value);
  }

  const { formatMessage } = useIntl();

  const [visible, setVisible] = useState(false);
  const [localIsTile, setLocalIsTile] = useState(false);
  const isShow = useMemo(() => {
    if (!operateOptions.includes('more')) {
      return true;
    }
    return showMore;
  }, [showMore]);

  const canSetFilterName = useRef(true);
  function changeCanSetFilterName() {
    canSetFilterName.current = false;
    setTimeout(() => {
      canSetFilterName.current = true;
    }, 300);
  }

  useEffect(() => {
    changeCanSetFilterName?.();
    if (isTile) return setLocalIsTile(true);
    if (isShow) setLocalIsTile(false);
    else {
      setTimeout(() => {
        setLocalIsTile(true);
      }, 200);
    }
  }, [isShow]);

  const resetTips = () => {
    setUpdateTipsShow(false);
    isUpdate.current = false;
  };
  const isUpdate = useRef(false);
  const backConfirm = (data?: any, isBtnConfirm = false, noCache = false) => {
    if (!isBtnConfirm) isUpdate.current = true;
    if (isBtnConfirm) resetTips();
    data = data || dealwithBackData(noCache) || {};
    confirm(data, isBtnConfirm);
    if (isBtnConfirm) {
      const realFilterName = filterName || getFilterName(gameInfo);

      saveFilterName(gameInfo, realFilterName!, 'B');
    }
  };

  const [updateTipsShow, setUpdateTipsShow] = useState(false);
  const maskClick = () => {
    if (isUpdate.current && updateTipsShow) {
      close?.();
      resetTips();

      return;
    }

    if (isUpdate.current) {
      setUpdateTipsShow(true);
    } else {
      close?.();
    }
  };

  const setFilterSelect = (name?: string) => {
    // 如果有名称，设置过滤器名称并调用相应的回调，然后直接返回
    if (name) {
      setFilterName?.(name);
      return;
    }

    // 如果没有名称，但是可以设置过滤器名称，则执行重置和保存操作
    if (canSetFilterName?.current) {
      setFilterName?.('');
      saveFilterName(gameInfo, undefined, 'C');
    }
  };

  const {
    renderComponentElement,
    resetForm,
    dealwithBackData,
    historyChange,
    loading,
  } = useRenderComponent({
    ...otherProps,
    filter,
    renderExcludeFilter,
    setConfirmDisable: changeConfirmDisabled,
    confirm: backConfirm,
    setFilterSelect,
    gameInfo,
    wholeGameInfo,
    userInfo,
    lang,
    noLimitation,
    filterName,
    isTile,
  });
  const reset = async (data?: { [key: string]: any }) => {
    await resetForm(data);
  };

  const fixedHeaderRef = useRef<any>(null);
  const saveFilter = () => {
    const { fixedHeader, oriFilterValue } = dealwithBackData(true) || {};
    fixedHeaderRef.current = beforeSaveFilterSetting?.({
      value: oriFilterValue,
      fixedHeader,
    });
    setVisible(true);
  };

  useImperativeHandle(confirmRef, () => {
    return {
      click: (noCache = false) => backConfirm(null, true, noCache),
      reset: (data: { [key: string]: any }) => reset(data),
      save: () => saveFilter(),
    } as any;
  });

  const filterBox = useRef<HTMLDivElement>(null);
  const [hideFilterBtn, setHideFilterBtn] = useState(false);

  function getElementsWidth(filterCommonElements?: NodeListOf<Element>) {
    return Array.from(filterCommonElements || [])?.reduce(
      (pre: number, cur: Element) => pre + (cur?.clientWidth || 0) + 10,
      0,
    );
  }

  useEffect(() => {
    function resizeWindows() {
      // 通过 filter 列表判断是否存在打包的筛选器，避免 DOM 查询
      const hasPackFilter = filter.some((key) => packGameStore.includes(key));
      // 如果有打包的筛选器，强制显示 show more 按钮
      if (hasPackFilter) {
        setHideFilterBtn(false);
        return;
      }

      const filterCommonElements = filterBox.current?.querySelectorAll(
        `.${FilterItemClassName}`,
      );
      const width = getElementsWidth(filterCommonElements);

      setHideFilterBtn((filterBox.current?.clientWidth || 0) > width);
    }
    // 初始化
    resizeWindows();

    window.addEventListener('resize', resizeWindows);
    return () => {
      window.removeEventListener('resize', resizeWindows);
    };
  }, []);

  const [disabledSaveBtn, setDisabledSaveBtn] = useState(true);

  function changeSaveBtnDisable(value: boolean) {
    toggleSaveBtnDisable?.(value);
    setDisabledSaveBtn(value);
  }

  function StopBtnElement() {
    if (!keyOperateShowOptions.includes('stop')) return null;

    const btn = (
      <Button type="primary" onClick={onStop} className="dbo-filter-stop-btn">
        <img src={StopIcon} className="dbo-stop-icon" />
        {formatMessage({ id: 'Stop' })}
      </Button>
    );

    return btn;
  }

  function ConfirmBtnElement() {
    if (!keyOperateShowOptions.includes('confirm')) return null;

    const btn = (
      <Button
        disabled={disabledConfirm || !!disabledTips}
        loading={confirmLoading}
        type="primary"
        onClick={() => {
          backConfirm(null, true);
          addSubmitBuriedPoint();
        }}
      >
        {formatMessage({ id: 'Confirm' })}
      </Button>
    );

    const params: any = { placement: 'top', showIcon: false };
    if (!!disabledTips) {
      return (
        <BubbleCommon {...params} tipsMap={disabledTips}>
          {btn}
        </BubbleCommon>
      );
    }

    if (updateTipsShow) {
      return (
        <BubbleCommon {...params} tipsMap={ChangeFilterShowTips} visible>
          {btn}
        </BubbleCommon>
      );
    }

    return btn;
  }

  function ResetBtnElement() {
    if (!keyOperateShowOptions.includes('reset')) return null;
    const btn = (
      <Button
        disabled={resetLoading || resetDisabled}
        loading={resetLoading}
        type="weak"
        onClick={() => void reset()}
      >
        {formatMessage({ id: resetBtnText || NO_DATE.EMPTY })}
      </Button>
    );

    if (resetBtnTips) {
      const params: any = { placement: 'top', showIcon: false };
      return (
        <BubbleCommon {...params} tipsMap={resetBtnTips}>
          {btn}
        </BubbleCommon>
      );
    }

    return btn;
  }

  function SaveBtnElement() {
    if (!operateOptions.includes('save')) return null;

    const btn = (
      <Button
        disabled={disabledSaveBtn}
        type="weak"
        onClick={() => saveFilter()}
      >
        {formatMessage({ id: 'Save Filter Setting' })}
      </Button>
    );

    if (disabledSaveBtn) {
      const text = formatMessage({ id: 'Up to 50 settings can be saved' });
      return (
        <BubbleCommon closeOnScroll dark tipsMap={null} content={text}>
          {btn}
        </BubbleCommon>
      );
    }

    return btn;
  }

  const FilterButton = () => {
    if (filterFixed) {
      return (
        <Button
          type="link"
          className="dbo-filterbox-btn"
          onClick={() => close?.()}
        >
          <div className="filterbox-right">
            <span>{formatMessage({ id: 'Close-1' })}</span>
            <SvgIcon type="point-top" className="filterbox-right-icon" />
          </div>
        </Button>
      );
    }

    if (operateOptions.includes('more') && !hideFilterBtn) {
      return (
        <Button
          type="link"
          className="dbo-filterbox-btn"
          onClick={toggleShowMore}
        >
          {formatMessage({ id: showMore ? 'Collapse Filter' : 'More Filters' })}
        </Button>
      );
    }

    return null;
  };

  return (
    <CardCommon
      className={classNames('filter-box-wrapper', {
        'dbo-filterbox-preview': isPreviews,
        'dbo-filterbox--all-preview': allPreview,
      })}
      bodyStyle={loading ? { minHeight: 112, lineHeight: '88px' } : {}}
      loading={loading}
    >
      <>
        <div
          className={`dbo-filterbox ${isShow ? 'show-all' : 'show-some'} ${
            lang === 'zh-CN' && 'dbo-filterbox-cn'
          }`}
        >
          {/* 这里使用loading添加一个loading态 */}
          <div className="dbo-filterbox-flex" ref={filterBox}>
            <div className="filter-common-wrapper">
              {operateOptions.includes('history') && (
                <div className={FilterItemClassName}>
                  <HistorySelect
                    filterName={filterName}
                    userInfo={userInfo}
                    setFilterName={setFilterName}
                    confirm={historyChange}
                    isMobile={false}
                    gameInfo={gameInfo}
                    finallyDealwithHistoryValue={finallyDealwithHistoryValue}
                    historyConfig={historyConfig}
                    setDisabledSaveBtn={changeSaveBtnDisable}
                    historyFilterChange={historyFilterChange}
                  ></HistorySelect>
                </div>
              )}
              {renderComponentElement}
              {endElement}
              {/* {operateOptions.includes('tips') ? tipsElement : null} */}
            </div>
          </div>
          <Justify
            left={
              <>
                <StopBtnElement></StopBtnElement>
                <ConfirmBtnElement></ConfirmBtnElement>
                <ResetBtnElement></ResetBtnElement>
                <SaveBtnElement></SaveBtnElement>
              </>
            }
            right={<FilterButton></FilterButton>}
          />
          <SavePopup
            lang={lang}
            gameInfo={gameInfo}
            showData={fixedHeaderRef.current?.fixedHeader}
            serverData={fixedHeaderRef.current?.value}
            saveOpen={visible}
            filter={filter}
            onlySaveFilters={onlySaveFilters}
            isAccountRegion={isAccountRegion}
            renderExcludeFilter={renderExcludeFilter}
            confirm={(filterItem: any, filterName: string) => {
              historyChange(filterItem, filterName);
            }}
            close={() => setVisible(!setVisible)}
          />
          <div className="mask" onClick={maskClick}></div>
        </div>
      </>
    </CardCommon>
  );
}
